import { useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, FileUp, Loader2, Mail } from "lucide-react";
import { EmailPreviewDialog } from "@/components/t4r/learners/EmailPreviewDialog";

const TONE = { create: "bg-green-100 text-green-800", update: "bg-sky-100 text-sky-800", skip: "bg-amber-100 text-amber-800", error: "bg-red-100 text-red-800" };

const PreviewTable = ({ rows }) => (
  <div className="max-h-[300px] overflow-y-auto rounded-md border border-border">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 sticky top-0"><tr className="text-left"><th className="px-2 py-1.5">Line</th><th className="px-2 py-1.5">Learner</th><th className="px-2 py-1.5">Courses</th><th className="px-2 py-1.5">Result</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.line} className="border-t border-border align-top" data-testid={`lms-import-row-${r.line}`}>
            <td className="px-2 py-1.5 text-muted-foreground">{r.line}</td>
            <td className="px-2 py-1.5"><p className="font-medium">{r.name || "—"}</p><p className="text-muted-foreground">{r.email}</p></td>
            <td className="px-2 py-1.5">{r.courses.join(", ") || "—"}</td>
            <td className="px-2 py-1.5"><Badge variant="outline" className={`text-[10px] capitalize ${TONE[r.status]}`}>{r.status}</Badge>{[...r.errors, ...r.warnings].map((m, i) => <p key={i} className={r.errors.includes(m) ? "text-red-700" : "text-muted-foreground"}>{m}</p>)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const LmsImportDialog = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const reset = () => { setFile(null); setPreview(null); };
  const template = async () => {
    try {
      const res = await api.get("/lms/admin/import/learners/template", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = "lms-learners-template.csv"; a.click();
    } catch (err) { toast.error("Could not download template"); }
  };
  const run = async (dryRun) => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/lms/admin/import/learners?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (!dryRun) { toast.success(`Imported: ${data.summary.create} new, ${data.summary.update} updated`); onImported(); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };
  const s = preview?.summary;
  const sample = preview?.rows?.find((r) => r.status === "create");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="lms-import-dialog">
        <DialogHeader>
          <DialogTitle>Import learners from CSV</DialogTitle>
          <DialogDescription className="text-xs">Columns: name, email, courses (exact course titles separated by ";"), send_invite (yes/no). Existing learners get the new courses added. Nothing is saved until you click Import.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1" onClick={template} data-testid="lms-import-template"><Download size={12} /> Download template</Button>
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-slate-50">
              <FileUp size={12} /> {file ? file.name : "Choose CSV"}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="lms-import-file" />
            </label>
            <Button size="sm" variant="outline" disabled={!file || busy} onClick={() => run(true)} data-testid="lms-import-preview">{busy ? <Loader2 size={12} className="animate-spin" /> : "Preview"}</Button>
          </div>
          {s && (
            <div className="flex flex-wrap gap-1.5 text-xs" data-testid="lms-import-summary">
              {["total", "create", "update", "skip", "error"].map((k) => <span key={k} className={`rounded-full px-2 py-0.5 ${TONE[k] || "bg-slate-100 text-slate-700"}`}>{s[k]} {k}</span>)}
              {!preview.dry_run && <span className="font-medium text-green-700">Import complete</span>}
              {sample && <button onClick={() => setPreviewing(true)} className="ml-auto inline-flex items-center gap-1 font-medium text-green-800 hover:underline" data-testid="lms-import-preview-email"><Mail size={12} /> Preview welcome email</button>}
            </div>
          )}
          {preview && <PreviewTable rows={preview.rows} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Close</Button>
          <Button className="bg-green-700 hover:bg-green-800 text-white" disabled={!preview?.dry_run || !(s.create + s.update) || busy} onClick={() => run(false)} data-testid="lms-import-commit">Import {s ? `(${s.create + s.update})` : ""}</Button>
        </DialogFooter>
        <EmailPreviewDialog open={previewing} onClose={() => setPreviewing(false)} name={sample?.name} courseTitles={sample?.courses || []} />
      </DialogContent>
    </Dialog>
  );
};
